/**
 * PB-AIT-3 — AI Text review ack (reuses aiReviewHop scoped fingerprint map).
 */

import { buildTextReviewFingerprint } from '@theme/pb-ai-text-logic';
import {
  clearReviewAcknowledgement as clearHopReviewAcknowledgement,
  formatScopedReviewKey,
  resolveActiveReviewScopeId,
} from '@theme/pb-ai-review-hop';

function escapeFieldLabelForSelector(label) {
  const str = String(label || '');
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(str);
  }
  return str.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function ensureHop(builderState) {
  if (!builderState) return null;
  if (!builderState.aiReviewHop || typeof builderState.aiReviewHop !== 'object') {
    builderState.aiReviewHop = {
      active: false,
      fieldLabel: '',
      returnStep: 0,
      returnMode: 'MAIN',
      reviewAcknowledged: false,
      reviewAcknowledgedByLabel: {},
      reviewAcknowledgedByScope: {},
    };
  }
  if (
    !builderState.aiReviewHop.reviewAcknowledgedByScope ||
    typeof builderState.aiReviewHop.reviewAcknowledgedByScope !== 'object'
  ) {
    builderState.aiReviewHop.reviewAcknowledgedByScope = {};
  }
  return builderState.aiReviewHop;
}

export function resolveTextStateForLabel(builderState, fieldLabel, stateOverride) {
  if (stateOverride && typeof stateOverride === 'object') return stateOverride;
  const label = fieldLabel ? String(fieldLabel) : '';
  if (!label) return null;
  if (typeof document !== 'undefined') {
    const selectors = ['#main-flow-container', '#sub-flow-container'];
    for (let i = 0; i < selectors.length; i += 1) {
      const wrap = document.querySelector(
        `${selectors[i]} .pb-ai-text-wrap[data-label="${escapeFieldLabelForSelector(label)}"]`
      );
      if (wrap && wrap._pbAiTextState) return wrap._pbAiTextState;
    }
  }
  const buckets = [];
  if (builderState?.aiTextByLabel?.byLabel?.[label]) {
    buckets.push(builderState.aiTextByLabel.byLabel[label]);
  }
  if (builderState?.sub?.aiTextByLabel?.byLabel?.[label]) {
    buckets.push(builderState.sub.aiTextByLabel.byLabel[label]);
  }
  for (let j = 0; j < buckets.length; j += 1) {
    const saved = buckets[j];
    if (saved && typeof saved === 'object') return saved;
  }
  return null;
}

export function acknowledgeTextReview(builderState, fieldLabel, stateOverride, scopeId) {
  const hop = ensureHop(builderState);
  if (!hop) return null;
  const label = fieldLabel ? String(fieldLabel) : '';
  if (!label) return hop;
  const state = resolveTextStateForLabel(builderState, label, stateOverride);
  const fingerprint = buildTextReviewFingerprint(state);
  if (!fingerprint) return hop;
  const scope = scopeId || resolveActiveReviewScopeId(builderState);
  hop.reviewAcknowledgedByScope[formatScopedReviewKey(scope, label)] = fingerprint;
  hop.fieldLabel = label;
  hop.reviewAcknowledged = true;
  return hop;
}

export function isTextReviewAcknowledged(builderState, fieldLabel, stateOverride, scopeId) {
  const hop = ensureHop(builderState);
  if (!hop) return false;
  const label = fieldLabel ? String(fieldLabel) : hop.fieldLabel || '';
  if (!label) return false;
  const scope = scopeId || resolveActiveReviewScopeId(builderState);
  const scopedKey = formatScopedReviewKey(scope, label);
  const ackFingerprint = hop.reviewAcknowledgedByScope[scopedKey];
  if (!ackFingerprint) return false;
  const state = resolveTextStateForLabel(builderState, label, stateOverride);
  const currentFingerprint = buildTextReviewFingerprint(state);
  if (!currentFingerprint) return false;
  return ackFingerprint === currentFingerprint;
}

export function clearTextReviewAcknowledgement(builderState, fieldLabel, scopeId) {
  return clearHopReviewAcknowledgement(builderState, fieldLabel, scopeId);
}

if (typeof globalThis !== 'undefined') {
  globalThis.PBAiTextReview = {
    resolveTextStateForLabel,
    acknowledgeTextReview,
    isReviewAcknowledged: isTextReviewAcknowledged,
    clearTextReviewAcknowledgement,
    buildTextReviewFingerprint,
  };
}
